import { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import categoryService, { type CreateCategoryData, type UpdateCategoryData } from '../services/category.service'
import type { Category } from '../types/category.types'

export const useCategoryActions = () => {
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingCategory, setEditingCategory] = useState<Category | null>(null)
    const queryClient = useQueryClient()

    // Create category mutation
    const createMutation = useMutation({
        mutationFn: (data: CreateCategoryData) => categoryService.createCategory(data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['categories'] })
            message.success('Category created successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to create category')
        },
    })

    // Update category mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateCategoryData }) => 
            categoryService.updateCategory(id, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['categories'] })
            message.success('Category updated successfully!')
            setIsFormModalOpen(false)
            setIsDetailDrawerOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update category')
        },
    })

    // Delete category mutation
    const deleteMutation = useMutation({
        mutationFn: (id: string) => categoryService.deleteCategory(id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['categories'] })
            message.success('Category deleted successfully!')
            setIsDetailDrawerOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete category')
        },
    })

    const handleViewDetails = (category: Category) => {
        setSelectedCategory(category)
        setIsDetailDrawerOpen(true)
    }

    const handleAddCategory = () => {
        setEditingCategory(null)
        setIsFormModalOpen(true)
    }

    const handleEditCategory = (category: Category) => {
        setEditingCategory(category)
        setIsFormModalOpen(true)
    }

    const handleDeleteCategory = (category: Category) => {
        Modal.confirm({
            title: 'Delete Category',
            content: `Are you sure you want to delete ${category.name}? This will affect ${category.productCount} products.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(category.id)
            },
        })
    }

    const handleFormSubmit = (values: any) => {
        if (editingCategory) {
            // Update existing category
            updateMutation.mutate({
                id: editingCategory.id,
                data: {
                    name: values.name,
                    slug: values.slug,
                    parent_id: values.parent_id || null,
                    description: values.description,
                    image: values.image,
                    status: values.status,
                },
            })
        } else {
            // Create new category
            createMutation.mutate({
                name: values.name,
                slug: values.slug,
                parent_id: values.parent_id || null,
                description: values.description,
                image: values.image,
                status: values.status || 'active',
            })
        }
    }

    const closeDrawer = () => setIsDetailDrawerOpen(false)
    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        selectedCategory,
        isDetailDrawerOpen,
        isFormModalOpen,
        editingCategory,
        handleViewDetails,
        handleAddCategory,
        handleEditCategory,
        handleDeleteCategory,
        handleFormSubmit,
        closeDrawer,
        closeFormModal,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
    }
}
